/**
 * @file Checkout.tsx
 * @description Checkout page with plan selection and success/error messages.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { toast } from "react-toastify";

import { auth } from "../../config/firebaseConfig";
import config from "../../config/config";
import log from "../../config/logging";

import AlphabetBackground from "../../components/common/AlphabetBackground";
import BackLink from "../../components/common/BackLink";
import GenerateContentButton from "../../components/common/GenerateContentButton";
import GeneratePlanButton from "../../components/common/GeneratePlanButton";
import CrownIcon from "../../components/common/icons/CrownIcon";

/**
 * PlanSelector component for selecting a subscription plan.
 */
const PlanSelector = () => {
  const [selectedPlan, setSelectedPlan] = useState<"monthly" | "annual">(
    "monthly"
  );
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const prices: Record<"monthly" | "annual", number> = {
    monthly: 15,
    annual: 150,
  };

  const handlePlanChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedPlan(event.target.value as "monthly" | "annual");
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user) {
      log.warn("User not authenticated, redirecting to login");
      toast.error("Per favore, accedi per visualizzare questa pagina");
      navigate("/accedi");
      return;
    }
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${config.backendUrl}/api/subscriptions/checkout`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ planType: selectedPlan }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            "Errore durante la creazione della sessione di checkout"
        );
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      log.error("Error creating checkout session:", error);
      toast.error("Si è verificato un errore. Riprova più tardi.");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="relative p-6 mx-auto bg-base-100 rounded-3xl xl:p-12 w-fit"
    >
      <fieldset className="mb-4">
        <legend className="mb-4 text-2xl font-light text-base-content">
          Seleziona il piano:
        </legend>
        <div className="flex flex-col justify-center gap-4 md:flex-row">
          <label className="flex-row text-base font-normal rounded-full form-control btn btn-outline border-gray-line hover:border-gray-line flex-nowrap text-blue-dark hover:text-blue-dark hover:bg-gray-light lg:btn-wide">
            <input
              type="radio"
              name="plan"
              value="monthly"
              checked={selectedPlan === "monthly"}
              onChange={handlePlanChange}
              className="radio checked:bg-blue-dark radio-xs"
            />
            <span>Mensile</span>
          </label>
          <label className="flex-row text-base font-normal rounded-full form-control btn btn-outline border-gray-line hover:border-gray-line flex-nowrap text-blue-dark hover:text-blue-dark hover:bg-gray-light lg:btn-wide">
            <input
              type="radio"
              name="plan"
              value="annual"
              checked={selectedPlan === "annual"}
              onChange={handlePlanChange}
              className="radio checked:bg-blue-dark radio-xs"
            />
            <span>Annuale</span>
          </label>
        </div>
      </fieldset>
      <div className="divider"></div>
      <div className="flex justify-between px-2 mb-4 text-2xl font-medium text-base-content">
        <span>Subtotale</span>
        <span>{prices[selectedPlan]}€</span>
      </div>
      <button
        className="px-6 mt-6 text-lg font-normal border-none rounded-full text-primary-content bg-primary btn btn-block"
        type="submit"
      >
        Procedi con il pagamento
      </button>
      <BackLink />
    </form>
  );
};

/**
 * SuccessMessage component for displaying a success message.
 */
const SuccessMessage = () => (
  <section className="relative flex flex-col items-center gap-4 p-6 mx-auto bg-base-100 rounded-3xl xl:p-12 w-fit">
    <h2 className="!mb-4 !text-2xl text-base-content">Ordine effettuato!</h2>
    <p>Riceverai una conferma via email.</p>
    <div className="flex flex-row gap-4 mt-6">
      <GenerateContentButton />
      <GeneratePlanButton />
    </div>
    <BackLink />
  </section>
);

/**
 * ErrorMessage component for displaying an error message.
 */
const ErrorMessage = () => (
  <section className="relative flex flex-col items-center gap-4 p-6 mx-auto bg-base-100 rounded-3xl xl:p-12 w-fit">
    <h2 className="!mb-0 !text-2xl text-base-content">Ordine annullato</h2>
    <p>
      Non ti è stato addebitato nessun importo. Puoi continuare ad utilizzare il
      tuo piano Free.
    </p>
    <GenerateContentButton className="mt-6 btn-wide" />
    <BackLink />
  </section>
);

/**
 * Checkout component for the Checkout page.
 */
const Checkout: React.FC = () => {
  const [status, setStatus] = useState<"success" | "error" | "idle">("idle");

  const cleanupUrl = useCallback(() => {
    const newUrl =
      window.location.origin + window.location.pathname + "#/checkout";
    window.history.replaceState({}, document.title, newUrl);
  }, []);

  useEffect(() => {
    const fullUrl = window.location.href;
    const url = new URL(fullUrl);
    const success = url.searchParams.get("success");
    const canceled = url.searchParams.get("canceled");

    if (success === "true" && status === "idle") {
      setStatus("success");
      toast.success("Abbonamento attivato con successo!", {
        onClose: () => {
          setTimeout(cleanupUrl, 500);
        },
      });
    } else if (canceled === "true" && status === "idle") {
      setStatus("error");
      toast.info("Processo di abbonamento annullato.", {
        onClose: () => {
          setTimeout(cleanupUrl, 500);
        },
      });
    }
  }, [status, cleanupUrl]);

  return (
    <section className="mx-auto text-center">
      <AlphabetBackground />
      <CrownIcon className="relative w-16 h-16 mx-auto mb-4 aspect-square" />
      <h2 className="text-2xl !font-medium text-center text-accent !mb-12 relative">
        Premium Plan
      </h2>
      {status === "success" && <SuccessMessage />}
      {status === "error" && <ErrorMessage />}
      {status === "idle" && <PlanSelector />}
    </section>
  );
};

export default Checkout;
